import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface DocumentCategorySelectProps {
  currentCategory: string;
  onCategoryChange: (category: string) => void;
  document: {
    id: string;
    user_id: string;
  };
}

export function DocumentCategorySelect({ 
  currentCategory, 
  onCategoryChange,
  document 
}: DocumentCategorySelectProps) {
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [predefinedCategories, setPredefinedCategories] = useState([
    "kontrakter",
    "avtaler",
    "korrespondanse",
    "rettslige_dokumenter",
    "personvern",
    "annet",
    "ukategorisert"
  ]);

  const categoryLabels: { [key: string]: string } = {
    kontrakter: "Kontrakt",
    avtaler: "Avtale",
    korrespondanse: "Korrespondanse",
    rettslige_dokumenter: "Rettslig dokument",
    personvern: "Personvern",
    annet: "Annet",
    ukategorisert: "Ukategorisert"
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    const normalizedCategory = newCategory.toLowerCase().replace(/\s+/g, '_');
    
    if (predefinedCategories.includes(normalizedCategory)) {
      toast.error("Denne kategorien eksisterer allerede");
      return;
    }

    try {
      // Update the document with the new category
      const { error } = await supabase
        .from('documents')
        .update({ category: normalizedCategory })
        .eq('id', document.id)
        .eq('user_id', document.user_id);

      if (error) throw error;

      setPredefinedCategories(prev => [...prev, normalizedCategory]);
      categoryLabels[normalizedCategory] = newCategory;
      onCategoryChange(normalizedCategory);
      setNewCategory("");
      setShowNewCategory(false);
      
      toast.success("Ny kategori lagt til");
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error("Kunne ikke legge til ny kategori");
    }
  };

  return (
    <div className="space-y-2">
      {showNewCategory ? (
        <div className="flex gap-2">
          <Input
            placeholder="Skriv ny kategori"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="flex-1"
          />
          <Button 
            size="sm" 
            onClick={handleAddCategory}
            disabled={!newCategory.trim()}
          >
            Legg til
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Select 
            value={currentCategory} 
            onValueChange={onCategoryChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Velg kategori" />
            </SelectTrigger>
            <SelectContent>
              {predefinedCategories.map((category) => (
                <SelectItem key={category} value={category}>
                  {categoryLabels[category] || category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowNewCategory(true)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}